<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { ddlHelper } from '@/utils'
import { organizationMethods } from '@/state/helpers'
import { organizationService } from '@/helpers/services/organization.service'

/**
 * Onboarding a brand.
 *
 * Create only — there is no edit. The two things that could be edited are
 * ai_bot's to change (`name` is operator-only in its API) and
 * the bot config, which belongs on the bot's own screens. The one locally
 * editable flag, "does the inbox take work for this brand", is a toggle on the
 * listing.
 *
 * ── What actually happens on submit ─────────────────────────────────────
 * The backend POSTs this to ai_bot, which validates the whole tenant config,
 * then refreshes the local mirror from the answer. Order matters: the other way
 * round leaves a brand in the picker that the bot refuses to serve, and the
 * agent who selects it sees an empty inbox with no explanation.
 */
export default {
  page: { title: i18n.t('label.createX', [i18n.t('module.organization')]) },
  components: { Layout, PageHeader, MultiSelect },
  data() {
    return {
      title: i18n.t('label.createX', [i18n.t('module.organization')]),
      breadcrumbItems: [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.organization'), href: '/organizations' },
        { text: i18n.t('label.createX', [i18n.t('module.organization')]), active: true },
      ],
      form: {
        name: '',
        botName: '',
        // The three a brand cannot be useful without: rag answers questions,
        // handoff is what makes this inbox receive anything at all, and
        // smalltalk stops a greeting being routed as an unknown intent.
        skills: ['rag', 'handoff', 'smalltalk'],
      },
      /*
       * The brand's own backend, filled in here rather than on a second visit.
       * Setting a client up is one job; making somebody create the brand, find
       * it in the list, open it and come back for the credentials is three.
       *
       * Optional — plenty of brands have no backend to connect, and a required
       * field would make them invent one.
       */
      cred: { endpoint: '', public_id: '', secret: '' },
      credWarning: '',
      /** Cal.com, shown only when Appointments is among the skills. The event
       *  types are the platform account's, fetched on demand. */
      appt: { calcom_event_type_id: null, calcom_user: '', timezone: 'Asia/Kuala_Lumpur', fallback_attendee_email: '' },
      calcom: { account: null, eventTypes: [], loading: false, error: '' },
      skillDdl: ddlHelper.getBotSkills(),
      submitting: false,
    }
  },
  computed: {
    wantsJk() {
      return this.form.skills.indexOf('jk') !== -1
    },
    wantsAppointment() {
      return this.form.skills.indexOf('appointment') !== -1
    },
    eventTypeOptions() {
      return [
        { value: null, text: this.$t('organization.apptPickEventType') },
        ...this.calcom.eventTypes.map((e) => ({ value: e.id, text: `${e.title} · ${e.lengthInMinutes || '?'} min · #${e.id}` })),
      ]
    },
  },
  watch: {
    wantsAppointment(on) {
      if (on && !this.calcom.eventTypes.length) this.loadCalcom()
    },
  },
  validations: {
    form: {
      name: { required },
    },
    /*
     * The URL is required the moment anything else in the JK block is filled.
     *
     * The whole block is optional — a brand may have no backend — but the
     * credential write is guarded on the endpoint, so an Access ID and a token
     * with no URL were silently dropped: the brand was created, the JK card
     * looked set up, and nothing had been saved. Nobody was told.
     */
    cred: {
      endpoint: {
        required: requiredIf(function () {
          return Boolean(this.cred.public_id || this.cred.secret)
        }),
      },
    },
  },
  methods: {
    ...organizationMethods,

    async loadCalcom() {
      this.calcom.loading = true
      this.calcom.error = ''
      try {
        const res = await organizationService.getCalcomEventTypes()
        this.calcom.eventTypes = (res && res.data) || []
        this.calcom.account = (res && res.account) || null
      } catch (e) {
        const d = e && e.response && e.response.data
        this.calcom.error = (d && d.error) || i18n.t('message.error')
      } finally {
        this.calcom.loading = false
      }
    },

    async submit() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.submitting = true

      this.credWarning = ''

      try {
        // The endpoint rides along so the server can refuse BEFORE the brand
        // is created — see OrganizationController::store. Without it a bad URL
        // leaves a brand behind and an error telling you to go and finish it.
        const appointment = this.wantsAppointment
          ? {
              ...(this.appt.calcom_event_type_id ? { calcom_event_type_id: this.appt.calcom_event_type_id } : {}),
              ...(this.appt.calcom_user ? { calcom_user: this.appt.calcom_user } : {}),
              ...(this.appt.timezone ? { timezone: this.appt.timezone } : {}),
              ...(this.appt.fallback_attendee_email ? { fallback_attendee_email: this.appt.fallback_attendee_email } : {}),
            }
          : null
        const result = await this.createOrganization({
          data: { ...this.form, jkEndpoint: this.wantsJk ? this.cred.endpoint : '', appointment },
        })
        if (!result) return

        const tenantId = result.data && result.data.tenant_id

        /*
         * The credential write comes after the brand, because it hangs off the
         * brand's id. That is why the ENDPOINT was checked on the way in: by
         * the time we are here the URL has already been accepted, so what is
         * left to go wrong is the database being down — rare, and worth a
         * sentence rather than a silent half-setup.
         */
        if (tenantId && this.wantsJk && this.cred.endpoint) {
          try {
            await organizationService.saveCredentials(tenantId, {
              endpoint: this.cred.endpoint,
              public_id: this.cred.public_id,
              ...(this.cred.secret ? { secret: this.cred.secret } : {}),
            })
          } catch (e) {
            const d = e && e.response && e.response.data
            const msg = d && typeof d.error === 'string' ? d.error : i18n.t('message.error')
            this.credWarning = i18n.t('organization.credSavedPartly', [msg])
            return
          }
        }

        this.$router.push({ name: 'Organizations-listing' })
      } finally {
        this.submitting = false
      }
    },

    cancel() {
      this.$router.push({ name: 'Organizations-listing' })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <!-- col-12, with the fields in a grid inside — see userForm for why
           a full-width card beats a narrow column and a stretched input. -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <!-- No brand-id field. ai_bot's tenant_id is AUTO_INCREMENT, so
                     the number is assigned on save and shown in the listing.
                     Asking an operator to invent one was the whole problem. -->
                <b-col cols="12" md="6" lg="5">
              <b-form-group :label="$t('label.name')" label-for="name">
                <b-form-input
                  id="name"
                  v-model.trim="form.name"
                  :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                />
                <b-form-invalid-feedback>
                  {{ $t('message.requiredX', [$t('label.name')]) }}
                </b-form-invalid-feedback>
                <small class="form-text text-muted">{{ $t('organization.nameHint') }}</small>
              </b-form-group>
                </b-col>

                <b-col cols="12" md="6" lg="3">
              <b-form-group :label="$t('organization.botName')" label-for="botName">
                <b-form-input id="botName" v-model.trim="form.botName" />
                <small class="form-text text-muted">{{ $t('organization.botNameHint') }}</small>
              </b-form-group>
                </b-col>

                <b-col cols="12">
              <b-form-group :label="$t('organization.botSkills')" label-for="skills">
                <MultiSelect
                  id="skills"
                  v-model="form.skills"
                  :options="skillDdl"
                  :hint="$t('organization.skillsHint')"
                />
              </b-form-group>
                </b-col>


              </b-row>

              <!-- Only the sections the chosen skills need. A brand without
                   the JK skill has no backend to connect; one without
                   Appointments has no calendar to pick. -->
              <template v-if="wantsAppointment">
                <h6 class="mt-4 mb-2">{{ $t('organization.apptTitle') }}</h6>
                <p class="small text-muted">
                  {{ $t('organization.apptCreateHint') }}
                  <span v-if="calcom.account && calcom.account.email"> {{ $t('organization.apptAccount', [calcom.account.email]) }}</span>
                </p>
                <b-alert v-if="calcom.error" show variant="warning" class="small">{{ calcom.error }}</b-alert>
                <b-row>
                  <b-col cols="12" md="6" lg="5">
                    <b-form-group :label="$t('organization.apptEventType')">
                      <b-form-select v-model="appt.calcom_event_type_id" :options="eventTypeOptions" :disabled="calcom.loading" />
                      <small class="form-text text-muted">{{ calcom.loading ? $t('label.loading') : $t('organization.apptEventTypePickHint') }}</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="$t('organization.apptCalcomUser')">
                      <b-form-input v-model.trim="appt.calcom_user" placeholder="e.g. jagau" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="$t('organization.apptFallbackEmail')">
                      <b-form-input v-model.trim="appt.fallback_attendee_email" type="email" />
                      <small class="form-text text-muted">{{ $t('organization.apptFallbackEmailShort') }}</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="$t('organization.apptTimezone')">
                      <b-form-input v-model.trim="appt.timezone" />
                    </b-form-group>
                  </b-col>
                </b-row>
              </template>

              <template v-if="wantsJk">
              <h6 class="mt-4 mb-2">{{ $t('organization.jkTitle') }}</h6>
              <p class="small text-muted">{{ $t('organization.jkOptional') }}</p>
              <b-row>
                <b-col cols="12" md="6" lg="5">
                  <b-form-group :label="$t('organization.jkEndpoint')">
                    <b-form-input
                      v-model.trim="cred.endpoint"
                      placeholder="https://api.example.com/v1"
                      :state="$v.cred.endpoint.$dirty ? !$v.cred.endpoint.$error : null"
                    />
                    <b-form-invalid-feedback>
                      {{ $t('organization.jkEndpointNeeded') }}
                    </b-form-invalid-feedback>
                    <small class="form-text text-muted">{{ $t('organization.jkEndpointHint') }}</small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6" lg="3">
                  <b-form-group :label="$t('organization.jkPublicId')">
                    <b-form-input v-model.trim="cred.public_id" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6" lg="4">
                  <b-form-group :label="$t('organization.jkSecret')">
                    <b-form-input v-model.trim="cred.secret" type="password" autocomplete="new-password" />
                    <small class="form-text text-muted">{{ $t('organization.jkSecretHint') }}</small>
                  </b-form-group>
                </b-col>
              </b-row>

              </template>

              <b-alert v-if="credWarning" show variant="warning" class="small">{{ credWarning }}</b-alert>

              <b-alert show variant="info" class="small">
                <i class="mdi mdi-information-outline mr-1"></i>
                {{ $t('organization.createNotice') }}
              </b-alert>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting">
                  {{ $t('label.submit') }}
                </b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
